import React, { useState, useEffect } from 'react';
import { Line, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, ComposedChart, Cell } from 'recharts';
import './styles/lineChart.css';
import moment from 'moment';
import { Typography } from '@mui/material';

interface ChartData {
  name: string;
  value: number;
}

interface CustomTooltipProps {
  active?: boolean;
  payload?: Array<{ value: number }>;
  coordinate?: { x: number; y: number };
}

interface LineChartProps {
  label: string;
  data?: ChartData[];
}

const defaultData: ChartData[] = [
  { name: 'Jan', value: 3000 },
  { name: 'Feb', value: 5000 },
  { name: 'Mar', value: 6000 },
  { name: 'Apr', value: 7000 },
  { name: 'May', value: 4000 },
  { name: 'Jun', value: 6000 },
  { name: 'Jul', value: 8000 },
];

// Custom tooltip component
const CustomTooltip: React.FC<CustomTooltipProps> = ({ active, payload, coordinate }) => {
  if (active && payload && payload.length) {
    const tooltipStyle: React.CSSProperties = {
      backgroundColor: '#FFFFFF',
      borderRadius: '10px',
      boxShadow: '0px 4px 10px rgba(0, 0, 0, 0.1)',
      padding: '10px',
      textAlign: 'center',
      fontSize: '14px',
      color: '#297041',
      position: 'absolute',
      minWidth: 72,
      top: coordinate ? coordinate.y - 40 : 0,
      left: coordinate ? coordinate.x - 40 : 0,
    };

    return (
      <div style={tooltipStyle}>
        <strong>{payload[0].value} $D</strong>
        <div style={{ color: '#A1A1A1', fontSize: '12px' }}>Points</div>
      </div>
    );
  }

  return null;
};

// Function to format values in k
const formatYAxisValue = (value: number): string => {
  if (value >= 1000) {
    return `${(value / 1000).toFixed()}k`;
  }
  return value.toString();
};

const LineChart: React.FC<LineChartProps> = ({ label, data = defaultData }) => {
  const [hoveredIndex, setHoveredIndex] = useState<number | null>(null);
  const [date, setDate] = useState<string>(moment().format('YYYY-MM-DD'));
  const [chartHeight, setChartHeight] = useState<number>(window.innerWidth <= 700 ? 195 : 240);

  useEffect(() => {
    const handleResize = () => {
      setChartHeight(window.innerWidth <= 700 ? 250 : 300);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  return (
    <div>
      <div className="headingRow">
        <Typography variant="h4" noWrap>
          {label}
        </Typography>
        {/* <input type="date" className="dateInput" value={date} onChange={(e) => setDate(e.target.value)} /> */}
      </div>

      <ResponsiveContainer width="100%" height={chartHeight}>
        <ComposedChart
          data={data}
          margin={{ top: 20, right: 20, left: 20, bottom: 5 }}
          onMouseLeave={() => setHoveredIndex(null)}
        >
          <defs>
            <linearGradient id="barGradient" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor="#297041" stopOpacity={1} />
              <stop offset="100%" stopColor="#FFFFFF" stopOpacity={1} />
            </linearGradient>
          </defs>
          <XAxis dataKey="name" />
          <YAxis tickFormatter={formatYAxisValue} />
          <Tooltip content={<CustomTooltip />} />

          <Bar dataKey="value" barSize={44} radius={[10, 10, 0, 0]}>
            {data.map((entry, index) => (
              <Cell
                key={`cell-${index}`}
                fill={hoveredIndex === index ? 'url(#barGradient)' : '#d3d3d3'}
                onMouseEnter={() => setHoveredIndex(index)}
                onMouseLeave={() => setHoveredIndex(null)}
              />
            ))}
          </Bar>
        </ComposedChart>
      </ResponsiveContainer>
    </div>
  );
};

export default LineChart;
